import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, TextField, MenuItem, Paper,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Dialog, DialogContent, IconButton, Divider, Stack, Chip, Alert, Avatar, Container
} from '@mui/material';
import {
  Add, Close, ArrowBack, Send, CheckCircle, AccessTime,
  InfoOutlined, WarningAmber, Error, PriorityHigh,
  SupportAgent, Description, Assessment
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from '../contexts/NotificationContext';
import axios from 'axios';

const API_URL = '/api';

const _SUP_ICON_MAP = { InfoOutlined, WarningAmber, Error, PriorityHigh };
const SupportSeverityIcon = ({ name }) => {
  const Icon = _SUP_ICON_MAP[name];
  return Icon ? <Icon /> : null;
};

const SEVERITY_OPTIONS = [
  { 
    value: 'low', 
    label: 'General guidance', 
    description: 'General questions about AWS services',
    icon: 'InfoOutlined',
    color: '#1d8102'
  },
  { 
    value: 'medium', 
    label: 'System impaired', 
    description: 'Non-critical functions behaving abnormally',
    icon: 'WarningAmber',
    color: '#f89406'
  },
  { 
    value: 'high', 
    label: 'Production system impaired', 
    description: 'Important functions impaired or degraded',
    icon: 'Error',
    color: '#d13212'
  },
  { 
    value: 'urgent', 
    label: 'Production system down', 
    description: 'Business significantly impacted',
    icon: 'PriorityHigh',
    color: '#8b0000'
  }
];

const CATEGORY_OPTIONS = [
  { value: 'technical-support', label: 'Technical Support' },
  { value: 'environment-setup', label: 'Environment Setup' },
  { value: 'consulting', label: 'Consulting Services' },
  { value: 'professional-services', label: 'Professional Services' },
  { value: 'billing', label: 'Billing & Account' },
  { value: 'feature-request', label: 'Feature Request' },
  { value: 'bug-report', label: 'Bug Report' },
  { value: 'other', label: 'Other' }
];

function SupportNew() {
  const [view, setView] = useState('list'); // 'list', 'create', 'detail'
  const [tickets, setTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [awsAccounts, setAwsAccounts] = useState([]);
  const [replyText, setReplyText] = useState('');
  const { user } = useAuth();
  const { addNotification } = useNotifications();

  const [newTicket, setNewTicket] = useState({
    subject: '',
    category: '',
    priority: 'medium',
    description: '',
    email: user?.email || '',
    phone: '',
    awsAccountId: ''
  });

  useEffect(() => {
    fetchTickets();
    fetchAwsAccounts();
  }, []);

  const fetchAwsAccounts = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/aws/accounts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAwsAccounts(response.data.accounts || []);
    } catch (error) {
      console.error('Error fetching AWS accounts:', error);
    }
  };

  const fetchTickets = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/support/tickets`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTickets(response.data.tickets || []);
    } catch (error) {
      console.error('Error fetching tickets:', error);
      addNotification({ message: 'Failed to load tickets', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTicket = async () => {
    if (!newTicket.subject || !newTicket.category || !newTicket.description || !newTicket.awsAccountId) {
      addNotification({ message: 'Please fill in all required fields', type: 'warning' });
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/support/tickets`, newTicket, {
        headers: { Authorization: `Bearer ${token}` }
      });

      addNotification({ message: 'Support case created successfully', type: 'success' });
      setView('list');
      setNewTicket({
        subject: '',
        category: '',
        priority: 'medium',
        description: '',
        email: user?.email || '',
        phone: '',
        awsAccountId: ''
      });
      fetchTickets();
    } catch (error) {
      console.error('Error creating ticket:', error);
      addNotification({ message: error.response?.data?.error || 'Failed to create case', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleViewTicket = async (ticket) => {
    setSelectedTicket(ticket);
    setView('detail');
    await fetchConversations(ticket.id);
  };

  const fetchConversations = async (ticketId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/support/tickets/${ticketId}/conversations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConversations(response.data.conversations || []);
    } catch (error) {
      console.error('Error fetching conversations:', error);
    }
  };

  const handleSendReply = async () => {
    if (!replyText.trim() || !selectedTicket) return;

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/support/tickets/${selectedTicket.id}/reply`,
        { message: replyText },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      addNotification({ message: 'Reply sent successfully', type: 'success' });
      setReplyText('');
      await fetchConversations(selectedTicket.id);
    } catch (error) {
      console.error('Error sending reply:', error);
      addNotification({ message: 'Failed to send reply', type: 'error' });
    }
  };

  const handleResolveTicket = async () => {
    if (!selectedTicket) return;

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/support/tickets/${selectedTicket.id}/resolve`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      addNotification({ message: 'Case marked as resolved', type: 'success' });
      setSelectedTicket({ ...selectedTicket, status: 'resolved' });
      fetchTickets();
    } catch (error) {
      console.error('Error resolving ticket:', error);
      addNotification({ message: 'Failed to resolve case', type: 'error' });
    }
  };

  const getSeverityColor = (priority) => {
    const colors = {
      low: '#1d8102',      // Green - low priority
      medium: '#f89406',   // Orange/Yellow - medium priority
      high: '#d13212',     // Red - high priority
      urgent: '#8b0000'    // Dark Red - urgent priority
    };
    return colors[priority] || '#545b64';
  };

  const getStatusColor = (status) => {
    const colors = {
      open: '#0073bb',       // Blue - open/active
      'in-progress': '#f89406', // Orange - in progress
      resolved: '#1d8102',   // Green - resolved/success
      closed: '#545b64'      // Gray - closed/inactive
    };
    return colors[status] || '#545b64';
  };

  // LIST VIEW
  if (view === 'list') {
    return (
      <Container maxWidth="xl">
        {/* Header */}
        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.primary', mb: 0.5 }}>
              Support Center
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Create and manage your support cases
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setView('create')}
          >
            Create case
          </Button>
        </Box>

          {/* Cases Table */}
          <Paper sx={{ bgcolor: 'background.paper', borderRadius: 2, overflow: 'hidden', boxShadow: 'none', border: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                Your support cases
              </Typography>
            </Box>

            {tickets.length === 0 ? (
              <Box sx={{ p: 6, textAlign: 'center' }}>
                <Typography variant="body1" sx={{ color: '#545b64', mb: 2 }}>
                  You don't have any support cases
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<Add />}
                  onClick={() => setView('create')}
                  sx={{
                    textTransform: 'none',
                    borderColor: '#545b64',
                    color: '#545b64',
                    '&:hover': { borderColor: '#16191f', bgcolor: '#fafafa' }
                  }}
                >
                  Create your first case
                </Button>
              </Box>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'background.secondary' }}>
                      <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.875rem' }}>Case ID</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.875rem' }}>Subject</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.875rem' }}>Category</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.875rem' }}>Severity</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.875rem' }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.875rem' }}>Created</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {tickets.map((ticket) => (
                      <TableRow
                        key={ticket.id}
                        hover
                        onClick={() => handleViewTicket(ticket)}
                        sx={{ 
                          cursor: 'pointer', 
                          '&:hover': { bgcolor: 'action.hover' } 
                        }}
                      >
                        <TableCell>
                          <Typography sx={{ color: 'primary.main', fontWeight: 600, fontSize: '0.875rem' }}>
                            {ticket.ticket_number || `#${ticket.id}`}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography sx={{ color: 'text.primary', fontSize: '0.875rem', fontWeight: 500 }}>
                            {ticket.subject}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
                            {CATEGORY_OPTIONS.find(c => c.value === ticket.category)?.label || ticket.category}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={ticket.priority.toUpperCase()}
                            size="small"
                            sx={{
                              bgcolor: getSeverityColor(ticket.priority),
                              color: '#fff',
                              fontWeight: 600,
                              fontSize: '0.75rem',
                              height: 24
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={ticket.status.toUpperCase()}
                            size="small"
                            sx={{
                              bgcolor: getStatusColor(ticket.status),
                              color: '#fff',
                              fontWeight: 600,
                              fontSize: '0.75rem',
                              height: 24
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
                            {new Date(ticket.created_at).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric'
                            })}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </Container>
    );
  }

  // CREATE VIEW
  if (view === 'create') {
    return (
      <Container maxWidth="md">
          {/* Header */}
          <Box sx={{ mb: 3 }}>
            <Button
              startIcon={<ArrowBack />}
              onClick={() => setView('list')}
              sx={{ mb: 2 }}
            >
              Back to cases
            </Button>
            <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.primary', mb: 0.5 }}>
              Create case
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Provide details about your issue
            </Typography>
          </Box>

          <Paper variant="outlined" sx={{ borderRadius: '12px', p: 3 }}>
            <Stack spacing={2.5}>
              {/* Subject */}
              <Box>
                <Typography sx={{ fontWeight: 600, color: 'text.primary', mb: 0.75, fontSize: '0.875rem' }}>
                  Subject *
                </Typography>
                <TextField
                  fullWidth size="small"
                  placeholder="Brief description of your issue"
                  value={newTicket.subject}
                  onChange={(e) => setNewTicket({ ...newTicket, subject: e.target.value })}
                />
              </Box>

              {/* Severity */}
              <Box>
                <Typography sx={{ fontWeight: 600, color: 'text.primary', mb: 0.75, fontSize: '0.875rem' }}>
                  Severity *
                </Typography>
                <TextField
                  select fullWidth size="small"
                  value={newTicket.priority}
                  onChange={(e) => setNewTicket({ ...newTicket, priority: e.target.value })}
                >
                  {SEVERITY_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Avatar sx={{ width: 28, height: 28, bgcolor: option.color, '& .MuiSvgIcon-root': { fontSize: 16 } }}>
                          <SupportSeverityIcon name={option.icon} />
                        </Avatar>
                        <Box>
                          <Typography sx={{ fontWeight: 600, fontSize: '0.875rem', color: 'text.primary' }}>{option.label}</Typography>
                          <Typography variant="caption" sx={{ color: 'text.secondary' }}>{option.description}</Typography>
                        </Box>
                      </Box>
                    </MenuItem>
                  ))}
                </TextField>
              </Box>

              {/* Category */}
              <Box>
                <Typography sx={{ fontWeight: 600, color: 'text.primary', mb: 0.75, fontSize: '0.875rem' }}>
                  Category *
                </Typography>
                <TextField select fullWidth size="small"
                  value={newTicket.category}
                  onChange={(e) => setNewTicket({ ...newTicket, category: e.target.value })}
                >
                  {CATEGORY_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                  ))}
                </TextField>
              </Box>

              {/* AWS Account */}
              <Box>
                <Typography sx={{ fontWeight: 600, color: 'text.primary', mb: 0.75, fontSize: '0.875rem' }}>
                  AWS Account *
                </Typography>
                <TextField select fullWidth size="small"
                  value={newTicket.awsAccountId}
                  onChange={(e) => setNewTicket({ ...newTicket, awsAccountId: e.target.value })}
                >
                  <MenuItem value="" disabled>Select an AWS account</MenuItem>
                  {awsAccounts.map((account) => (
                    <MenuItem key={account.id} value={account.account_id}>
                      {account.account_name} ({account.account_id})
                    </MenuItem>
                  ))}
                </TextField>
              </Box>

              {/* Description */}
              <Box>
                <Typography sx={{ fontWeight: 600, color: 'text.primary', mb: 0.75, fontSize: '0.875rem' }}>
                  Description *
                </Typography>
                <TextField fullWidth multiline rows={6} size="small"
                  placeholder="Describe your issue in detail..."
                  value={newTicket.description}
                  onChange={(e) => setNewTicket({ ...newTicket, description: e.target.value })}
                />
              </Box>

              {/* Contact Info */}
              <Box>
                <Typography sx={{ fontWeight: 600, color: 'text.primary', mb: 0.75, fontSize: '0.875rem' }}>
                  Contact email *
                </Typography>
                <TextField fullWidth size="small" type="email"
                  value={newTicket.email}
                  onChange={(e) => setNewTicket({ ...newTicket, email: e.target.value })}
                />
              </Box>

              <Box>
                <Typography sx={{ fontWeight: 600, color: 'text.primary', mb: 0.75, fontSize: '0.875rem' }}>
                  Phone (optional)
                </Typography>
                <TextField fullWidth size="small"
                  placeholder="+1 (555) 123-4567"
                  value={newTicket.phone}
                  onChange={(e) => setNewTicket({ ...newTicket, phone: e.target.value })}
                />
              </Box>

              <Divider />

              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button variant="outlined" onClick={() => setView('list')}>Cancel</Button>
                <Button variant="contained" onClick={handleCreateTicket} disabled={loading}>
                  {loading ? 'Creating...' : 'Submit'}
                </Button>
              </Box>
            </Stack>
          </Paper>
        </Container>
    );
  }

  // DETAIL VIEW
  if (view === 'detail' && selectedTicket) {
    return (
      <Container maxWidth="xl">
        <Box sx={{ maxWidth: 1000, mx: 'auto' }}>
          {/* Header */}
          <Box sx={{ mb: 3 }}>
            <Button startIcon={<ArrowBack />} onClick={() => setView('list')} sx={{ mb: 2 }}>
              Back to cases
            </Button>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 0.5 }}>
              <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.primary' }}>
                Case {selectedTicket.ticket_number || `#${selectedTicket.id}`}
              </Typography>
              <Chip label={selectedTicket.status.toUpperCase()}
                sx={{ bgcolor: getStatusColor(selectedTicket.status), color: '#fff', fontWeight: 600 }} />
            </Box>
            <Typography variant="h6" sx={{ color: 'text.primary', mb: 0.5 }}>
              {selectedTicket.subject}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Created {new Date(selectedTicket.created_at).toLocaleDateString('en-US', {
                month: 'long', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit'
              })}
            </Typography>
          </Box>

          {/* Case Details */}
          <Paper variant="outlined" sx={{ borderRadius: '12px', p: 2.5, mb: 2.5 }}>
            <Typography sx={{ fontWeight: 600, color: 'text.primary', mb: 2, fontSize: '0.9375rem' }}>
              Case details
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: '180px 1fr', gap: 1.5 }}>
              <Typography sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>Severity:</Typography>
              <Chip label={selectedTicket.priority.toUpperCase()} size="small"
                sx={{ bgcolor: getSeverityColor(selectedTicket.priority), color: '#fff', fontWeight: 600, width: 'fit-content' }} />
              <Typography sx={{ color: '#545b64' }}>Category:</Typography>
              <Typography sx={{ color: '#16191f' }}>
                {CATEGORY_OPTIONS.find(c => c.value === selectedTicket.category)?.label || selectedTicket.category}
              </Typography>
              <Typography sx={{ color: '#545b64' }}>Description:</Typography>
              <Typography sx={{ color: '#16191f', whiteSpace: 'pre-wrap' }}>
                {selectedTicket.description}
              </Typography>
            </Box>
          </Paper>

          {/* Correspondence */}
          <Paper sx={{ bgcolor: '#fff', borderRadius: 2, boxShadow: '0 1px 1px rgba(0,0,0,0.1)' }}>
            <Box sx={{ p: 3, borderBottom: '1px solid #e9ebed' }}>
              <Typography sx={{ fontWeight: 600, color: '#16191f' }}>
                Correspondence
              </Typography>
            </Box>

            {/* Messages */}
            <Box sx={{ p: 3, maxHeight: 500, overflowY: 'auto' }}>
              {conversations.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography sx={{ color: '#545b64' }}>
                    No messages yet. Our support team will respond shortly.
                  </Typography>
                </Box>
              ) : (
                <Stack spacing={3}>
                  {conversations.map((conv, index) => (
                    <Box key={index}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Typography sx={{ fontWeight: 600, color: '#16191f', fontSize: '0.875rem' }}>
                          {conv.from_customer ? 'You' : 'Shellkode Support'}
                        </Typography>
                        <Typography sx={{ color: '#545b64', fontSize: '0.75rem' }}>
                          {new Date(conv.created_at).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </Typography>
                      </Box>
                      <Paper sx={{
                          p: 2,
                          bgcolor: conv.from_customer ? 'background.secondary' : 'primary.light',
                          border: '1px solid',
                          borderColor: conv.from_customer ? 'divider' : 'primary.main',
                          borderRadius: '8px',
                        }}>
                        <Typography sx={{ color: 'text.primary', fontSize: '0.875rem', whiteSpace: 'pre-wrap' }}>
                          {conv.message}
                        </Typography>
                      </Paper>
                    </Box>
                  ))}
                </Stack>
              )}
            </Box>

            {/* Reply Section */}
            {selectedTicket.status !== 'resolved' && selectedTicket.status !== 'closed' && (
              <Box sx={{ p: 2.5, borderTop: '1px solid', borderColor: 'divider', bgcolor: 'background.secondary' }}>
                <Typography sx={{ fontWeight: 600, color: 'text.primary', mb: 1.5, fontSize: '0.875rem' }}>
                  Add correspondence
                </Typography>
                <TextField fullWidth multiline rows={4} size="small"
                  placeholder="Type your message..."
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  sx={{ mb: 2 }}
                />
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'space-between' }}>
                  <Button variant="outlined" color="success" startIcon={<CheckCircle />} onClick={handleResolveTicket}>
                    Resolve case
                  </Button>
                  <Button variant="contained" endIcon={<Send />} onClick={handleSendReply} disabled={!replyText.trim()}>
                    Send
                  </Button>
                </Box>
              </Box>
            )}

            {(selectedTicket.status === 'resolved' || selectedTicket.status === 'closed') && (
              <Box sx={{ p: 2.5, borderTop: '1px solid', borderColor: 'divider' }}>
                <Alert severity="success">
                  <Typography variant="body2">
                    This case has been {selectedTicket.status}. Thank you for contacting support.
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    If you experience any further issues, please feel free to open a new support case.
                  </Typography>
                </Alert>
              </Box>
            )}
          </Paper>
        </Box>
      </Container>
    );
  }

  return null;
}

export default SupportNew;
